import React from 'react'
import {
  Box,
  Button,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

interface OnboardingTaskModalProps {
  open: boolean
  onClose: (event?: any, reason?: string) => void
  title: string
  subtitle?: string
  completedCount?: number
  totalSteps?: number
  progress?: number
  steps?: any[]
  renderStep?: (step: any, index: number) => React.ReactNode
  footerButtonText: string
  onFooterButtonClick: () => void
  isNested?: boolean
  children?: React.ReactNode
  loading?: boolean
  headerVariant?: 'progress' | 'subPage'
  footerButtonDisabled?: boolean
  hideCloseIcon?: boolean
  minHeight?: string
}

const OnboardingTaskModal = ({
  open,
  onClose,
  title,
  subtitle,
  completedCount,
  totalSteps,
  progress,
  steps,
  renderStep,
  footerButtonText,
  onFooterButtonClick,
  isNested = false,
  children,
  loading = false,
  headerVariant = 'progress',
  footerButtonDisabled = false,
  hideCloseIcon = false,
  minHeight,
}: OnboardingTaskModalProps) => {
  const isMobile = useMediaQuery('(max-width:600px)')

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(5px)',
        zIndex: isNested ? 1400 : 1300,
      }}>
      <Box
        sx={{
          background: '#ffffff',
          width: isMobile ? '95%' : '620px',
          minHeight: minHeight || ((children || isNested) ? 'auto' : '613px'),
          maxHeight: '90vh',
          borderRadius: '19px',
          outline: 'none',

          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'none',
          fontFamily: 'InterMedium', fontWeight: 400,
          '& *': { fontFamily: 'InterMedium', fontWeight: 400 },
          display: 'flex',
          flexDirection: 'column',
        }}>
        {headerVariant === 'subPage' ? (
          <Box sx={{ 
            paddingLeft: '24px', paddingRight: '24px', height: '72px', fontFamily: 'Inter, sans-serif !important', borderBottom: '1px solid #E3E2E9', bgcolor: '#F9F9F9', mb: 4, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '48px', color: '#3C4252' }}>
            <Button
              onClick={onClose}
              startIcon={<ChevronLeftIcon />}
              sx={{
                color: '#3C4252',
                textTransform: 'none',
                fontFamily: 'InterMedium', fontWeight: 400,
                fontSize: '16px',
                '&:hover': { background: 'transparent' }
              }}
            >
              Back
            </Button>
            <Typography sx={{ fontFamily: 'InterMedium', fontWeight: 500, color: '#3A155A', fontSize: '20px', flex: 1, textAlign: 'center', lineHeight: '27px', letterSpacing: '0px', verticalAlign: 'middle' }}>
              {title}
            </Typography>
            {!hideCloseIcon && (
              <IconButton
                onClick={onClose}
                sx={{ color: '#3C4252' }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        ) : (
          <div style={{paddingLeft: '24px', paddingRight: '24px',paddingTop: '24px'}}>
            {!hideCloseIcon && (
              <IconButton
                onClick={onClose}
                sx={{ position: 'absolute', right: 16, top: 16, color: '#3C4252' }}>
                <CloseIcon />
              </IconButton>
            )}

            <Typography variant="h5" sx={{ 
              fontFamily: 'InterMedium', fontWeight: 500, 
              fontSize: '20px', 
              color: '#3A155A', 
              mb: 1,
              lineHeight: '27px',
              letterSpacing: '0px',
              verticalAlign: 'middle'
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ 
                color: '#3C4252', 
                mb: 4, 
                mt: "8px", 
                fontSize: '16px',
                fontFamily: 'InterMedium', fontWeight: 400,
                lineHeight: '100%',
                letterSpacing: '0px'
              }}>
                {subtitle}
              </Typography>
            )}
          </div>
        )}
        
        {headerVariant === 'progress' && progress !== undefined && completedCount !== undefined && totalSteps !== undefined && (
          <div style={{padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop:'14px', paddingBottom:'14px'}}>
            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'InterMedium', fontWeight: 400, color: '#1D2333' }}>
                {completedCount} of {totalSteps} complete
                <span style={{ color: '#3C4252', fontFamily: 'InterMedium', fontWeight: 400, marginLeft: '5px' }}>
                  ({Math.round(progress)}%)
                </span>
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#DDDEE4',
                border: 'none',
                outline: 'none',
                mb: 4,
                mt: '14px',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1B86E8',
                  borderRadius: 4,
                  border: 'none',
                  outline: 'none',
                },
              }}
            />
          </div>
        )}

        <Box sx={{ 
          paddingLeft: '24px', 
          paddingRight: '24px',
          mt: children ? (headerVariant === 'subPage' ? 0 : 2) : 0,
          flexGrow: 1,

          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#dadce0', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#bdc1c6' },
        }}>
          {children ? (
            children
          ) : (
            <Box
              sx={{
                background: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #DDDEE4',
                overflow: 'hidden',
              }}>
              {steps?.map((step, index) => (
                <React.Fragment key={step.id || index}>
                  {renderStep?.(step, index)}
                  {index < steps.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
        <div style={{padding: '24px'}}>
          <Button
            fullWidth
            variant="contained"
            disabled={loading || footerButtonDisabled}
            onClick={onFooterButtonClick}
            sx={{
              height: '48px',
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              fontFamily: 'InterMedium', fontWeight: 400,
              background: 'linear-gradient(180deg, #1E89ED 0%, #0A71D9 100%)',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(180deg, #1E89ED 0%, #0A71D9 100%)',
                boxShadow: 'none',
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                background: '#E5E5E5',
                color: '#A3A3A3',
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },
            }}>
            {footerButtonText}
          </Button>
        </div>

      </Box>
    </Modal>
  )
}

export default OnboardingTaskModal
